// Day-detail BottomSheet (DESIGN §2 screen 2 + §6).
//
// Mandatory `useSheetVariant` per DESIGN §7.1 — ADR-0038/0039 made
// "mobile → sheet, desktop → modal" a standing rule and the hook is what
// stops each caller from re-deriving the breakpoint.
//
// Its job is to **explain the day in words**, which is why shared time appears
// as a sentence ("9/11–9/13 你們都休假") rather than as a tint on the grid.
// DESIGN §5.2 gives three reasons and the third is decisive: a tinted cell
// cannot be distinguished from "something is booked".
//
// The list is exhaustive and untruncated even though the grid caps at
// `max_per_day` — the cap is a rendering budget for the cell, not a limit on
// what the user can read.

import { BottomSheet } from "@/components/patterns/BottomSheet";
import {
  CATEGORY_ICON,
  CATEGORY_LABEL,
  OWNER_LABEL,
  chipAriaLabel,
  chipClasses,
} from "@/components/calendar/calendarIcons";
import { CycleLogPanel } from "@/components/calendar/CycleLogPanel";
import { isPeriodEve } from "@/components/calendar/cyclePeriod";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import type { CalendarDay, CalendarItem } from "@/lib/api/calendar";
import { cn } from "@/lib/utils/cn";

/**
 * W58.5 — 这一天該不該出現「雙人行程聯動貼士」？
 *
 * **條件兩條，都要成立：**
 *
 * 1. **當日有共同行程 / 旅行。** `trip`（行程）與 `leave`（休假）兩類 ——
 *    兩者都是「兩個人一起不在家」的意思。
 * 2. **當天處於經期 / 排卵期 / 經期前夕。**
 *
 * **「經期」與「經期前夕」用事實，不用推算。** `cycle_state.actual` 是
 * 「有一筆記錄的經期事件覆蓋這一天」——那是**真的**。而推算的
 * `phase` 只是猜的；拿猜的階段去催人保暖，是把一個猜當成事實。
 *
 * **「排卵期」用推算是可以的** —— 排卵期本來就**只有**推算這一個版本
 * （沒有任何「實際排卵日」的事實存在），而它在受孕窗口裡，是最需要
 * 提醒「注意休息」的那一段。
 */
function hasTripHint(day: CalendarDay): boolean {
  const hasTrip = day.items.some(
    (item) => item.category === "trip" || item.category === "leave",
  );
  if (!hasTrip) return false;

  const state = day.cycle_state;
  // 經期 —— **事實**（有一筆記錄的經期事件覆蓋這一天）。
  if (state?.actual) return true;
  // 排卵期 —— **推算**（受孕窗口裡最需要提醒休息的一段）。
  if (state?.phase === "ovulation" || state?.ovulation_day) return true;
  // 經期前夕 —— 距離經期開始 ≤ 2 天。
  return isPeriodEve(day.cycle_inference, day.date);
}

export interface DaySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  day: CalendarDay | null;
  /** Q21 bidirectional entry: jump to the source record. */
  onOpenItem: (item: CalendarItem) => void;
  onOpenCycle: () => void;
  onEditItem: (item: CalendarItem) => void;
  /**
   * W58.5 — 「標記今日經期開始」。開一個今天是週期類型的表單。
   */
  onStartPeriod?: () => void;
}

export function DaySheet({
  open,
  onOpenChange,
  day,
  onOpenItem,
  onOpenCycle,
  onEditItem,
  onStartPeriod,
}: DaySheetProps) {
  const { variant, desktopWidth, isMobile } = useSheetVariant({ desktopWidth: 440 });

  // W58.5 — **雙人行程聯動貼士的條件。**
  //
  // 1. 當日有共同行程 / 旅行（`trip` 或 `leave` 類別的事件）；
  // 2. 當天處於**經期 / 排卵期 / 經期前夕**。
  //
  // **「經期前夕」** = 距離**實際**經期開始 ≤ 2 天 —— 而「實際經期」是
  // **事實**，所以這裡用的是 `cycle_state.actual`，不是推算。
  //
  // **不是 hook**（`day` 可能是 `null`，而 hook 必須在每次渲染時同一個順序
  // 被呼叫）—— 所以是一個純函式。
  const tripHint = day ? hasTripHint(day) : false;

  if (!day) return null;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      // W58.3 — **90%（`large` detent）。**
      //
      // 這張面板現在裝的東西比 W40 時多了一倍：週期階段（週期第 N 天、
      // 階段名）、推算的三個數字、每日經期紀錄（流量 / 痛經 / 症狀 /
      // 備註）。60vh 開起來，使用者看到的第一屏只有標題與一個數字，
      // 得先拖高才讀得到他打開這張面板要看的東西。
      //
      // 90vh 之後仍然可以往下拖、仍然可以收合（detent 模型不變），
      // 只是**打開時就已經是它想要的樣子**。
      initialDetent="large"
      className={cn(
        "overflow-y-auto",
        !isMobile && `w-[${desktopWidth}px]`,
      )}
      data-testid="calendar-day-sheet-body"
    >
      <div className="flex flex-col gap-s-4" data-testid="calendar-day-sheet">
        <header>
          <h2 className="text-h2 text-ink-1">
            {Number(day.date.slice(5, 7))} 月 {Number(day.date.slice(8, 10))} 日 · {day.weekday}
          </h2>
          <p className="mt-s-1 text-body-sm text-ink-2" data-testid="calendar-day-summary">
            {day.summary}
          </p>
        </header>

        {/*
          W58.3 — **重排：週期狀態在前，當日安排在後。**

          以前是「安排 → 週期」，而那是在這張面板只有事件可講的年代排的。
          現在面板裡有兩種完全不同的內容：

          - **週期狀態** —— 身體的事（週期第 N 天、推算、今天的身體感覺）；
          - **當日安排** —— 計畫的事（共同時間、事件）。

          身體狀態先講，因為它是**這一天的前提**：知道今天在週期的哪裡，
          才讀得懂「今天有個飯局」是排在經期第 3 天還是安全期。Meetyou 的
          日詳情也是這個順序（經期資訊在最上面）。

          兩段各自帶一個小標，90vh 之下才不會是一條長列表。
        */}
        {day.overlap.length > 0 || day.items.length > 0 ? (
          <section className="flex flex-col gap-s-3" data-testid="calendar-day-plan-section">
            <h3 className="text-micro uppercase tracking-wide text-ink-3">當日安排</h3>

            {day.overlap.length > 0 ? (
              <ul className="flex flex-col gap-s-2" data-testid="calendar-day-overlap">
                {day.overlap.map((o) => (
                  <li
                    key={`${o.start_date}-${o.end_date}`}
                    className="rounded-sm bg-shared-tint px-s-3 py-s-2 text-body-sm text-shared-strong"
                  >
                    {o.label}（{o.days} 天）
                  </li>
                ))}
              </ul>
            ) : null}

            {day.items.length > 0 ? (
              <ul
                className="flex flex-col divide-y divide-hairline"
                data-testid="calendar-day-items"
              >
                {day.items.map((item, i) => (
                  <li key={`${item.category}-${item.owner}-${i}`} className="py-s-3">
                    <div className="flex items-center gap-s-3">
                      <span
                        className={cn(
                          "flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[5px]",
                          chipClasses(item.owner),
                        )}
                        aria-hidden="true"
                      >
                        {(() => {
                          const Icon = CATEGORY_ICON[item.category];
                          return Icon ? <Icon size={11} strokeWidth={2} /> : null;
                        })()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body text-ink-1">
                          {item.title}
                          {item.count > 1 ? (
                            <span className="ml-s-2 text-meta text-ink-3">共 {item.count} 項</span>
                          ) : null}
                        </p>
                        <p className="mt-s-1 flex flex-wrap items-center gap-s-2 text-meta text-ink-3">
                          <span>{OWNER_LABEL[item.owner]}</span>
                          <span className="rounded-pill bg-paper-deep px-s-2 py-s-0">
                            {CATEGORY_LABEL[item.category]}
                          </span>
                          <span>{item.all_day ? "全日" : "有時刻"}</span>
                        </p>
                      </div>
                    </div>
                    <div className="mt-s-2 flex gap-s-2 pl-s-6">
                      <button
                        type="button"
                        // `min-h-11 min-w-11 px-1 -mx-1` — the same shape the W39.0
                        // pass used for 「查看全部」, with `min-w-11` added because
                        // that link measured 122px wide and this one does not: the
                        // label is two CJK glyphs, ~24px, so `min-h-11` alone leaves
                        // the width under the floor. The guard caught exactly that —
                        // it read `width=no size class` and failed rather than
                        // accepting `px-1` as if padding were a width.
                        //
                        // These are text-only buttons, and this pair is the ONLY
                        // route from the month grid to an event's editor (查看來源 /
                        // 編輯), so a 24x18 target is a real barrier on a phone. The
                        // negative margin keeps the row's left edge where it was.
                        className="min-h-11 min-w-11 px-1 -mx-1 text-meta font-semibold text-you-strong hover:underline"
                        onClick={() => onOpenItem(item)}
                        aria-label={`${chipAriaLabel({
                          category: item.category,
                          owner: item.owner,
                          allDay: item.all_day,
                          count: item.count,
                        })}，查看來源`}
                        data-testid="calendar-day-item-source"
                      >
                        {item.id ? "查看" : "前往來源"}
                      </button>
                      {item.id ? (
                        <button
                          type="button"
                          className="min-h-11 min-w-11 px-1 -mx-1 text-meta font-semibold text-ink-2 hover:underline"
                          onClick={() => onEditItem(item)}
                          data-testid="calendar-day-item-edit"
                        >
                          編輯
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        {/*
          **W58.4 —— 週期狀態移到當日安排的下面。**

          W58.3 我把它排在了前面，理由是「身體狀態是這一天的前提」。使用者不
          買帳：「**bottom sheet 週期狀態要放在每日事件下面**」。

          他是對的，而我的理由錯在**把面板當成週期面板**。這張面板的主體是
          **事件** —— 它是「日詳情」，一個有安排的日子先講安排了什麼，週期
          狀態是補充。而且事件列表現在是**可掃讀的清單**（時間、標題、誰、
          類型），週期狀態卻是一張要慢慢讀的卡片 —— 卡片壓在清單前面會擋住
          「今天有什麼」這個問題的答案。
        */}
        {/*
          W58.5 — **條件多了一個 `cycle_state`。**

          以前是 `cycle_inference || cycle_log` —— 而 `cycle_inference` 是
          **推算**，所以在「有實際經期、但記錄不足所以沒有推算」的日子
          （**冷啟動**—— 記錄只有一兩筆時正是這樣）整個週期狀態區塊
          **不渲染**，於是**每日紀錄的表單在那一天根本開不了**。

          而 `cycle_state` 講的是**系統對這一天知道什麼**：有實際經期、有
          推算、或有流量紀錄。「今天有沒有在經期」與「能不能推算」是兩件事。
        */}
        {day.cycle_inference || day.cycle_log || day.cycle_state ? (
          <section className="flex flex-col gap-s-3" data-testid="calendar-day-cycle-section">
            <h3 className="text-micro uppercase tracking-wide text-ink-3">週期狀態</h3>

            {day.cycle_inference ? (
              <section
                className="rounded-md border border-hairline bg-paper-deep p-s-3"
                data-testid="calendar-day-cycle"
              >
                <h4 className="text-micro uppercase tracking-wide text-ink-3">週期推斷</h4>
                {/* W58.0 — 這一天是週期第幾天、什麼階段。
                    以前這一段只在「落在推斷經期帶內」的日子出現，而且只講
                    上次 / 間隔 / 預估下次。現在落在任何一個投影週期內就出現，
                    而且多講一句對使用者真正有用的話：**今天在週期的哪裡**。
                    「今天可能在經期」和「今天是週期第 12 天 · 卵泡期」是兩句
                    強度不同的話，後者才是排程會用到的那句。 */}
                {day.cycle_inference.phase ? (
                  <p
                    className="mt-s-2 text-body-sm text-ink-1"
                    data-testid="calendar-day-cycle-phase"
                  >
                    {day.cycle_inference.cycle_day
                      ? `週期第 ${day.cycle_inference.cycle_day} 天 · `
                      : ""}
                    {/* W58.1 — 安全期是「卵泡期 + 黃體期」。安全期在月曆格上
                        **不畫任何記號**（它就是「什麼都沒有」的日子），
                        所以名字要在這裡講清楚。 */}
                    {day.cycle_inference.safe
                      ? `安全期 · ${day.cycle_inference.phase_label}`
                      : day.cycle_inference.phase_label}
                  </p>
                ) : null}
                <dl className="mt-s-2 grid grid-cols-3 gap-s-2">
                  <div>
                    <dt className="text-micro text-ink-3">上次</dt>
                    <dd className="text-body-sm font-semibold tabular-nums text-ink-1">
                      {day.cycle_inference.last_start.slice(5)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-micro text-ink-3">間隔</dt>
                    <dd className="text-body-sm font-semibold tabular-nums text-ink-1">
                      {day.cycle_inference.interval_days} 天
                    </dd>
                  </div>
                  <div>
                    <dt className="text-micro text-ink-3">預估下次</dt>
                    <dd className="text-body-sm font-semibold tabular-nums text-ink-1">
                      {day.cycle_inference.predicted_start.slice(5)}
                    </dd>
                  </div>
                </dl>
                <p className="mt-s-2 text-meta text-ink-2">
                  {/* W58.5 — **與週期頁同一句。** 以前這裡寫「推算出來的，
                      不一定準」，而週期頁有兩句、這裡一句 —— 三份同一個免責
                      聲明。DATA_MODEL §5 的重量要求它**存在**，不要求它
                      **重複**；而且這一句不再是「警告」，是「接下來的話」。 */}
                  推算只是參考，多記幾次會更準。
                </p>
                <button
                  type="button"
                  // The **only** route to the cycle record since W40.1 F4 took
                  // the inference band's tap target away. It renders exactly when
                  // `day.cycle_inference` exists — the same condition that draws the
                  // band on the month cell — so nothing that was one tap away
                  // became two steps or unreachable.
                  //
                  // Same treatment as the 查看來源 / 編輯 pair above, and for the
                  // same reason: a text-only button with no size class measures
                  // 24x18. This one was missed when those two were fixed — the
                  // class shape is identical, it just sits 60 lines further down
                  // the same file. Found by a repo-wide scan for size-less
                  // controls, not by reading this file, which is why
                  // a11y-control-size.guard.test.ts now exists.
                  className="mt-s-2 min-h-11 min-w-11 px-1 -mx-1 text-meta font-semibold text-you-strong hover:underline"
                  onClick={onOpenCycle}
                  data-testid="calendar-day-cycle-link"
                >
                  查看週期紀錄
                </button>
              </section>
            ) : null}

            {/* W58.1 — 每日經期伴隨紀錄。
                **只在跟週期有關的日子出現**：有推算（週期內）或本來就記過。
                每一天都放一個這麼大的面板會把日詳情變成一張表單，
                而那不是「解釋這一天」這件事的重點。

                面板在**還沒記過**的日子也要出現 —— 那正是要記錄的日子，
                而它是一個空白表單，不是「已有資料的顯示」。

                **W58.4 —— `owner` 由後端判定**，面板不再問「你要記誰的」。 */}
            <CycleLogPanel
              date={day.date}
              log={day.cycle_log}
              owner={day.cycle_owner ?? day.cycle_log?.owner ?? "me"}
              // W58.5 — **「實際經期」是事實**（`cycle_state.actual`），
              // 不是推算。前端不從 `cycle_inference` 去猜（ADR-0211）。
              inPeriod={day.cycle_state?.actual ?? false}
              tripHint={tripHint}
              onStartPeriod={onStartPeriod}
            />
          </section>
        ) : null}

        {/* W48.0 — **the empty case renders nothing here.**
          This branch used to print 「這天沒有安排」 as its own paragraph, directly
          under a header whose `day.summary` says **the same words**: the backend
          returns that exact string when the caller can see no items and no shared
          time (`calendar/service/views.py`, and
          `test_calendar_acceptance.py::test_s9_*` pins it because the summary must
          not leak a count). The live sheet showed the sentence twice, one line
          apart, which reads as a rendering fault rather than as an empty day.

          Removing the paragraph also fixes the *other* case rather than papering
          over this one. A day with shared time but no items has a summary of
          「N 段共同時間」 — there the paragraph was not a duplicate, but it was
          **wrong**: the day does have something on it, and the overlap list below
          the header is what actually enumerates it. So the honest rule is the
          one this branch now encodes: the summary sentence *is* the empty state,
          and the list is either present or absent.

          The count assertion lives in `Calendar.cta.test.tsx` — a duplicate here
          is invisible to every structural test, because two identical `<p>`s are
          perfectly valid DOM. */}
      </div>
    </BottomSheet>
  );
}
